import { memo, useCallback, useEffect } from 'react';
import {
  withDelay,
  withTiming,
  withSequence,
  useSharedValue,
  useAnimatedStyle,
} from 'react-native-reanimated';
import { Box, useLayout, Image } from '@bonanza-social/moonlight';
import { StatusSchema } from '../../../services/StatusService/StatusService.schemas';
import { StatusDemotionAnimationProps } from './StatusDemotionAnimation.types';
import StatusGemGlow from '../../StatusGemGlow/StatusGemGlow';

const { options: allStatuses } = StatusSchema;

function StatusDemotionAnimation({
  playerStatus,
  onAnimationEnd,
}: StatusDemotionAnimationProps) {
  const [layout, onLayout] = useLayout();
  const opacitySV = useSharedValue(0);
  const translateSV = useSharedValue(0);
  const playerStatusIndex = StatusSchema.options.indexOf(playerStatus);
  const nextPlayerStatus = StatusSchema.options[playerStatusIndex + 1];

  const animatedStyle = useAnimatedStyle(
    () => ({
      opacity: opacitySV.value,
      transform: [{ translateX: translateSV.value }],
    }),
    [layout.width],
  );
  const items =
    playerStatus === 'Member' ? allStatuses : [playerStatus, nextPlayerStatus];

  const applyAnimation = useCallback(() => {
    opacitySV.value = withDelay(
      500,
      withTiming(1, undefined, () => {
        translateSV.value = withDelay(
          500,
          withSequence(
            withTiming(translateSV.value - 50),
            withTiming(0, undefined, onAnimationEnd),
          ),
        );
      }),
    );
  }, [opacitySV, translateSV, onAnimationEnd]);

  useEffect(() => {
    if (!layout.width) {
      return;
    }

    const translate = -(layout.width * (items.length - 1));

    translateSV.value = translate;

    applyAnimation();
  }, [layout.width, items.length, playerStatus, translateSV, applyAnimation]);

  if (playerStatusIndex === -1 || !nextPlayerStatus) {
    return;
  }

  return (
    <Box
      flex={1}
      flexDirection="row"
      paddingVertical="space-1000"
      animatedStyle={animatedStyle}
      onLayout={onLayout}
    >
      {items.map((item) => {
        return (
          <Box
            key={item}
            opacity={playerStatus === 'Member' && item !== 'Member' ? 0.4 : 1}
            width="100%"
            centerContent
          >
            <Box position="relative">
              <Image name={`status.${item}Gem`} width={200} height={200} />
              <StatusGemGlow status={item} />
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

export default memo(StatusDemotionAnimation);
